// Completeness of an event ticket DEFINITION (customers/{cid}/eventtickets/{id}), as opposed to
// src/eventtickets.js which is about the purchases made against one.
//
// KEEP THIS FILE FREE OF REACT AND FIREBASE, like utils/benefitcards.js and utils/teamties.js.
//
// WHY THIS EXISTS. On 2026-09-11 a Paytrail callback took 86,00 EUR by Apple Pay for one
// "HPP Open 17.11.2026 TIISTAI - Perhelippu (2+2)", wrote the payment and the purchase, and then
// threw before generating the pdf or sending the confirmation:
//
//   purchases []
//   TypeError: Cannot read properties of undefined (reading 'paymenttoken')
//       at _generatePdfTicket (eventtickets-utils.js:99:37)
//
// The buyer was charged and got nothing, and the function still returned 200 so nobody was told.
// The cause was the ticket document, not the code path: the family ticket was flagged
// isCombination 'true', but the two variations meant to be its members carried
// combinations: [{ combinationId: '', quantity: 2 }] - an empty reference. The backend expands a
// combination by looking for the siblings that point BACK at it (paymentv3.js:1830-1862):
//
//   const includedVariations = eventticket.variations.filter(
//     (v) => v.combinations && v.combinations.find((c) => c.combinationId === variant.id))
//   ...
//   if (_orderLines.length !== orderLines.length) { orderLines.length = 0; orderLines.push(..._orderLines) }
//
// so nobody pointing at it means includedVariations [], which empties orderLines, which makes
// purchases[0] undefined. Every 'error' below is one of those - a fault that costs a real customer
// a real ticket after their money is gone. Everything else is a 'warning': the sale still works,
// it is just probably not what the admin meant.
//
// The editor cannot express any of this (the rjsf schema has no required arrays and FalconModal
// ignores ajv anyway), so this module is the only thing standing between a mistyped ticket and a
// failed payment. It is read by EventTicketsContainer in two places: the list banner + card chip,
// and live in the edit modal footer.

export const SEVERITY_ERROR = 'error'
export const SEVERITY_WARNING = 'warning'

// availableinshop is stored as the STRING 'true' / 'false' (see schemas/eventticket.js), and
// 'false' is truthy, so a plain truthiness check showed the chip on every ticket. Older documents
// may hold a real boolean, so accept both. Lives here rather than in the container because the
// in-shop-only checks below need exactly the same semantics and two copies would drift.
export const isInShop = (ticket) =>
  !!ticket && (ticket.availableinshop === true || ticket.availableinshop === 'true')

// isCombination is the STRING 'true', and paymentv3.js compares against the string. Matching the
// backend matters more than being helpful here: a variation carrying a real boolean true is NOT
// expanded by the backend, so treating it as a combination would hide the fault rather than report
// it. Variations created before the combination feature have no isCombination key at all.
const isCombinationVariation = (v) => !!v && v.isCombination === 'true'

const isNumber = (n) => typeof n === 'number' && isFinite(n)

const variationsOf = (ticket) =>
  ticket && Array.isArray(ticket.variations) ? ticket.variations.filter(Boolean) : []

const combinationsOf = (v) => (v && Array.isArray(v.combinations) ? v.combinations.filter(Boolean) : [])

const label = (v, idx) => (v && v.name) || 'lippuvaihtoehto ' + (idx + 1)

// The target of one combinations[] row, resolved the way the SAVE path resolves it.
// firebase.resolveCombinationIds maps the picked NAME onto an id at save time, so in the editor a
// row the admin just picked legitimately still has combinationId ''. Resolving by id first and by
// name second means the footer does not scream at a correct row, and a stored document with a
// stale name but a good id still passes.
const resolveCombinationTarget = (entry, variations) => {
  if (!entry) return null
  if (entry.combinationId) {
    const byId = variations.find((v) => v.id && v.id === entry.combinationId)
    if (byId) return byId
  }
  if (entry.combination) {
    const byName = variations.find((v) => v.name && v.name === entry.combination)
    if (byName) return byName
  }
  return null
}

const problem = (code, severity, message, v, idx) => ({
  code,
  severity,
  variationId: (v && v.id) || null,
  variationName: v ? label(v, idx) : null,
  message,
})

const parseDate = (value) => {
  if (!value) return null
  const t = new Date(value).getTime()
  return isFinite(t) ? t : null
}

// options.draft = true when validating LIVE form data in the editor. Ids are assigned by
// createEventTicket / updateEventTicket, and combinationId is resolved from the picked name, both
// at save time - so a row the admin added a second ago has neither and must not be reported.
export const validateEventTicket = (eventticket, options) => {
  const ticket = eventticket || {}
  const draft = !!(options && options.draft)
  const variations = variationsOf(ticket)
  const problems = []

  /* ---------- ticket level ---------- */

  if (!ticket.name) {
    problems.push(problem('ticket-name-missing', SEVERITY_WARNING,
      'Tapahtumalta puuttuu nimi. Nimi on vahvistusviestin ja PDF-lipun otsikko.'))
  }
  if (!ticket.start) {
    problems.push(problem('ticket-start-missing', SEVERITY_WARNING,
      'Tapahtumalta puuttuu alkuaika. Lippujen myynti päättyy oletuksena tapahtuman alkaessa, joten myynnille ei tule päättymisaikaa.'))
  }
  const salesStart = parseDate(ticket.ticketsalesstart)
  const salesEnd = parseDate(ticket.ticketsalesend)
  if (salesStart !== null && salesEnd !== null && salesEnd <= salesStart) {
    problems.push(problem('sales-window-inverted', SEVERITY_WARNING,
      'Lippujen myynti päättyy ennen kuin se alkaa.'))
  }

  if (isInShop(ticket)) {
    if (variations.length === 0) {
      problems.push(problem('no-variations', SEVERITY_WARNING,
        'Tapahtuma on verkkokaupassa, mutta siinä ei ole yhtään lippuvaihtoehtoa.'))
    } else {
      // A single variation at 0 is normal - that one is sold out on purpose. Only a ticket with
      // nothing at all left to sell is worth saying anything about.
      const available = variations.reduce(
        (sum, v) => sum + (isNumber(v.ticketsavailable) ? v.ticketsavailable : 0), 0)
      if (available === 0) {
        problems.push(problem('no-tickets-available', SEVERITY_WARNING,
          'Tapahtuma on verkkokaupassa, mutta yhtään lippua ei ole saatavilla.'))
      }
    }
    if (!isNumber(ticket.vat)) {
      problems.push(problem('vat-missing', SEVERITY_WARNING,
        'Tapahtumalta puuttuu ALV. Se välittyy maksunvälittäjälle ja kirjanpitoon.'))
    }
  }

  // resolveEventTicketExtraInfo (firebase.js) keys its title -> id map on the title, so two
  // questions sharing one title make a variation's selection land on whichever was mapped last.
  const extrainfo = Array.isArray(ticket.extrainfo) ? ticket.extrainfo.filter(Boolean) : []
  const seenTitles = {}
  extrainfo.forEach((e) => {
    if (!e.title) return
    if (seenTitles[e.title]) {
      if (seenTitles[e.title] === 1) {
        problems.push(problem('extrainfo-title-duplicate', SEVERITY_WARNING,
          'Kaksi lisätietokysymystä on samalla otsikolla "' + e.title +
          '". Lippuvaihtoehtojen valinnat voivat kohdistua väärään kysymykseen.'))
      }
      seenTitles[e.title] += 1
    } else {
      seenTitles[e.title] = 1
    }
  })
  const extrainfoKeys = extrainfo.map((e) => e.id).filter(Boolean)

  /* ---------- ids ---------- */

  if (!draft) {
    const seenIds = {}
    variations.forEach((v, idx) => {
      if (!v.id) {
        problems.push(problem('variation-id-missing', SEVERITY_ERROR,
          'Lippuvaihtoehdolta "' + label(v, idx) +
          '" puuttuu tunniste. Sen ostaminen epäonnistuu maksun jälkeen. Avaa tapahtuma ja tallenna se uudelleen.',
          v, idx))
        return
      }
      if (seenIds[v.id]) {
        problems.push(problem('variation-id-duplicate', SEVERITY_ERROR,
          'Lippuvaihtoehdoilla "' + seenIds[v.id] + '" ja "' + label(v, idx) +
          '" on sama tunniste. Myynti ja yhdistelmäliput kohdistuvat väärään lippuun.', v, idx))
      } else {
        seenIds[v.id] = label(v, idx)
      }
    })
  }

  /* ---------- names ---------- */

  // resolveCombinationIds keys idByName on the name, so a duplicate name involving a combination
  // makes the picker save against the wrong variation - that one is an error, not a warning.
  // Plain duplicates only confuse mergeTicketsForReporting, which merges them into one product.
  const byName = {}
  variations.forEach((v, idx) => {
    if (!v.name) {
      problems.push(problem('variation-name-missing', SEVERITY_WARNING,
        'Lippuvaihtoehdolta puuttuu nimi. Nimi näkyy verkkokaupassa ja tulostuu PDF-lippuun, eikä nimetöntä lippua voi valita yhdistelmälipuksi.',
        v, idx))
      return
    }
    if (!byName[v.name]) byName[v.name] = []
    byName[v.name].push({ v, idx })
  })
  Object.keys(byName).forEach((name) => {
    const group = byName[name]
    if (group.length < 2) return
    const withCombination = group.some((g) => isCombinationVariation(g.v))
    problems.push(problem(
      'variation-name-duplicate',
      withCombination ? SEVERITY_ERROR : SEVERITY_WARNING,
      withCombination
        ? 'Kaksi lippuvaihtoehtoa on nimeltään "' + name +
          '", ja toinen niistä on yhdistelmälippu. Yhdistelmälipun valinta tallentuu väärään lippuun — nimeä toinen uudelleen.'
        : 'Kaksi lippuvaihtoehtoa on nimeltään "' + name +
          '". Myyntiraportit yhdistävät ne samaksi tuotteeksi.',
      group[1].v, group[1].idx))
  })

  /* ---------- per variation ---------- */

  // Which combination variations somebody actually points at. Collected while walking the
  // combinations rows so the two checks cannot disagree about what counts as a reference.
  const referenced = {}

  variations.forEach((v, idx) => {
    const name = label(v, idx)

    if (!isNumber(v.price) || v.price < 0) {
      problems.push(problem('variation-price-missing', SEVERITY_WARNING,
        'Lippuvaihtoehdolta "' + name +
        '" puuttuu hinta tai se on virheellinen. Nollahinta on sallittu, mutta se on merkittävä nollaksi.', v, idx))
    }

    // soldcount is not part of the document: EventTicketsContainer puts it on each variation from
    // soldPurchases (src/eventtickets.js) before validating. The backend refuses every further sale
    // of an oversold variation - seen 2026-10-29, "Kutsuvieraslippu 2" lowered from 600 to 154
    // with 163 already sold.
    if (isNumber(v.soldcount) && isNumber(v.ticketsavailable) && v.soldcount > v.ticketsavailable) {
      problems.push(problem('variation-oversold', SEVERITY_WARNING,
        'Lippuvaihtoehtoa "' + name + '" on myyty ' + v.soldcount + ', mutta saatavilla on vain ' +
        v.ticketsavailable + '. Uusia lippuja ei voi myydä, ennen kuin saatavilla on enemmän kuin myyty.', v, idx))
    }

    const hasEbPrice = isNumber(v.earlybirdprice)
    if (hasEbPrice && !v.earlybirddeadline) {
      problems.push(problem('earlybird-deadline-missing', SEVERITY_WARNING,
        'Lippuvaihtoehdolle "' + name +
        '" on annettu early bird -hinta ilman takarajaa, joten alennus ei pääty koskaan.', v, idx))
    }
    if (!hasEbPrice && v.earlybirddeadline) {
      problems.push(problem('earlybird-price-missing', SEVERITY_WARNING,
        'Lippuvaihtoehdolle "' + name +
        '" on annettu early bird -takaraja ilman hintaa, joten alennusta ei anneta.', v, idx))
    }
    if (hasEbPrice && isNumber(v.price) && v.earlybirdprice >= v.price) {
      problems.push(problem('earlybird-price-not-lower', SEVERITY_WARNING,
        'Lippuvaihtoehdon "' + name + '" early bird -hinta ei ole normaalihintaa halvempi.', v, idx))
    }

    // Before the ids exist the selection holds the question TITLE, which is what
    // resolveEventTicketExtraInfo maps at save time - so a title is as valid as an id here.
    if (Array.isArray(v.extrainfo)) {
      v.extrainfo.filter(Boolean).forEach((key) => {
        if (extrainfoKeys.indexOf(key) !== -1) return
        if (extrainfo.some((e) => e.title === key)) return
        problems.push(problem('variation-extrainfo-unknown', SEVERITY_WARNING,
          'Lippuvaihtoehto "' + name +
          '" kysyy lisätietoa, jota ei ole tapahtuman lisätietokysymyksissä. Kysymystä ei kysytä ostajalta.', v, idx))
      })
    }

    const rows = combinationsOf(v)

    // Expansion is one level only (paymentv3.js returns [orderLine] for a non-combination and
    // never recurses), so a combination that is itself a member would put a row on the buyer's
    // pdf that is not a ticket anybody can use.
    if (isCombinationVariation(v) && rows.length > 0) {
      problems.push(problem('combination-nested', SEVERITY_ERROR,
        '"' + name +
        '" on yhdistelmälippu, joka on merkitty kuulumaan toiseen yhdistelmälippuun. Lippuja puretaan vain yksi taso, joten ostaja saisi rivin, joka ei ole oikea lippu.',
        v, idx))
    }

    rows.forEach((entry, row) => {
      const n = row + 1
      const target = resolveCombinationTarget(entry, variations)

      if (!target) {
        if (entry.combinationId) {
          problems.push(problem('combination-target-unknown', SEVERITY_ERROR,
            'Lippuvaihtoehto "' + name + '" viittaa yhdistelmälippuun, jota ei enää ole (rivi ' + n +
            '). Valitse yhdistelmälippu uudelleen.', v, idx))
        } else {
          problems.push(problem('combination-target-missing', SEVERITY_ERROR,
            'Lippuvaihtoehdolta "' + name + '" puuttuu yhdistelmälipun valinta (rivi ' + n +
            '). Valitse yhdistelmälippu tai poista rivi — muuten yhdistelmälipun osto epäonnistuu maksun jälkeen eikä ostaja saa lippua.',
            v, idx))
        }
      } else if (!isCombinationVariation(target)) {
        problems.push(problem('combination-target-not-combination', SEVERITY_ERROR,
          'Lippuvaihtoehto "' + name + '" viittaa lippuun "' + (target.name || '(nimetön)') +
          '", joka ei ole yhdistelmälippu. Rivi ei koskaan toimi — korjaa valinta.', v, idx))
      } else {
        referenced[target.id || target.name] = true
      }

      // The quantity is printed on the buyer's pdf ("Sisältää N henkilön sisäänpääsyn") and read
      // straight out of the row by paymentv3, so anything but a positive whole number is wrong.
      if (!isNumber(entry.quantity) || entry.quantity <= 0 || Math.floor(entry.quantity) !== entry.quantity) {
        problems.push(problem('combination-quantity-invalid', SEVERITY_ERROR,
          'Lippuvaihtoehdon "' + name + '" yhdistelmäriviltä puuttuu kelvollinen määrä (rivi ' + n +
          '). Määrä tulostuu ostajan PDF-lippuun.', v, idx))
      }
    })
  })

  // THE INCIDENT. A combination nobody points at expands to nothing, which empties orderLines and
  // leaves _generatePdfTicket reading purchases[0] of an empty array - after the card is charged.
  variations.forEach((v, idx) => {
    if (!isCombinationVariation(v)) return
    if (referenced[v.id || v.name]) return
    problems.push(problem('combination-has-no-members', SEVERITY_ERROR,
      'Yhdistelmälippuun "' + label(v, idx) +
      '" ei kuulu yhtään lippuvaihtoehtoa. Sen ostaminen epäonnistuu maksun jälkeen: ostajalta veloitetaan, mutta lippua tai vahvistusviestiä ei lähetetä. Merkitse mukaan kuuluvat liput.',
      v, idx))
  })

  // Errors first, so the banner and the modal footer lead with what actually breaks a sale.
  // Stable within a severity - the walk order above is the order of the form.
  const errors = problems.filter((p) => p.severity === SEVERITY_ERROR)
  const warnings = problems.filter((p) => p.severity !== SEVERITY_ERROR)
  return errors.concat(warnings)
}

export const hasBlockingProblems = (eventticket, options) =>
  validateEventTicket(eventticket, options).some((p) => p.severity === SEVERITY_ERROR)

export const countProblems = (problems) => {
  const list = problems || []
  return {
    errors: list.filter((p) => p && p.severity === SEVERITY_ERROR).length,
    warnings: list.filter((p) => p && p.severity === SEVERITY_WARNING).length,
  }
}
